import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import { ptListTasks } from "../../../services/personalTasks";
import {
  PRIORITIES,
  dayKey,
  fmtDayLabel,
  priorityBadge,
  statusBadge,
  taskDate,
} from "./taskHelpers";

// ─────────────────────────────────────────────────────────────────────────────
// Work List (Reports → My Tasks → Work List) — a compact, read-only index of
// the super-admin's tasks: title, date, priority. Open work (Pending / In
// Progress) is listed first, finished work drops to the bottom; newest date
// first within each group. Clicking a row opens the task detail page.
// ─────────────────────────────────────────────────────────────────────────────

const WorkList = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priority, setPriority] = useState("");

  useEffect(() => {
    if (!isSuperAdmin) return;
    setLoading(true);
    ptListTasks({ view: "all", ...(priority ? { priority } : {}) })
      .then((r) => setTasks(r.data?.tasks || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [isSuperAdmin, priority]);

  if (!isSuperAdmin) {
    return (
      <>
        <PageTitle activeMenu="Work List" motherMenu="Reports" />
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            This page is available to super-admin only.
          </div>
        </div>
      </>
    );
  }

  // Stable partition: open work on top, completed at the bottom, keeping the
  // server's newest-first date order inside each group. A task moves down on
  // its own as soon as it is marked Complete.
  const isDone = (t) => t.completed || t.status === "Complete";
  const sortedTasks = [
    ...tasks.filter((t) => !isDone(t)),
    ...tasks.filter(isDone),
  ];

  return (
    <>
      <PageTitle activeMenu="Work List" motherMenu="Reports" />

      <div className="card">
        <div className="card-header d-flex flex-wrap align-items-center gap-2">
          <h4 className="card-title mb-0 me-auto">
            Work List ({tasks.length})
          </h4>
          <select
            className="form-select form-select-sm w-auto"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate("/tasks")}
          >
            ← My Tasks
          </button>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Title</th>
                  <th style={{ width: 200 }}>Date</th>
                  <th style={{ width: 110 }}>Priority</th>
                  <th style={{ width: 120 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Loading…
                    </td>
                  </tr>
                ) : tasks.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No tasks yet.
                    </td>
                  </tr>
                ) : (
                  sortedTasks.map((task, idx) => {
                    const d = taskDate(task);
                    const sameDayAsPrev =
                      idx > 0 &&
                      dayKey(taskDate(sortedTasks[idx - 1])) === dayKey(d);
                    return (
                      <tr
                        key={task._id}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/tasks/${task._id}`)}
                      >
                        <td className="text-muted">{idx + 1}</td>
                        <td
                          className={
                            task.completed
                              ? "text-decoration-line-through text-muted"
                              : "fw-semibold"
                          }
                        >
                          {task.title}
                        </td>
                        <td className={sameDayAsPrev ? "text-muted" : ""}>
                          {fmtDayLabel(d)}
                        </td>
                        <td>
                          <span className={priorityBadge(task.priority)}>
                            {task.priority}
                          </span>
                        </td>
                        <td>
                          <span className={statusBadge(task.status)}>
                            {task.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkList;
